// The badge of a révision, read from its counters (chantier 7). It lives in
// lib/compta so the server (Tableaux de bord, lot D5a) classifies a dossier
// exactly as the Portefeuille shows it; components/pod/cahier/format.ts
// re-exports it for the screens.

export type ChipColor =
  | "success"
  | "warning"
  | "info"
  | "highlight"
  | "primary";

// The cahier's letterhead badge (chantier 7) — DERIVED from the counters, never
// stored, so no mission-level review state can drift out of sync. It is the line
// a confrère and a contrôle qualité read FIRST, so it must never overstate.
//
// Three rules the métier review made non-negotiable:
//  - "Touched" means a HUMAN acted on THIS pass: verdicts inherited from an
//    earlier mission (carriedForward) do not count, or a re-run after a FEC
//    re-import would announce "revue en cours" for a machine-only pass.
//  - Green is only for "nothing left to tell the client": a dossier where twelve
//    anomalies were RETAINED is reviewed, but it is not green — otherwise the
//    badge rewards the gesture that documents the least (retaining costs no
//    motive) and the letterhead lies.
//  - A mission that did not complete can still be reviewed (its completed cycles
//    produced real findings) — say so rather than freezing on "non revu".
export function reviewBadge(item: {
  status: string;
  findingCount: number;
  openFindingCount: number;
  validatedFindingCount: number;
  carriedForwardFindingCount: number;
  humanVerdictCount: number;
  completedCycleCount: number;
  concludedCycleCount: number;
}): { color: ChipColor; label: string } {
  if (item.status === "cancelled") {
    // There was no pass at all: "premier passage" would be false.
    return { color: "primary", label: "Mission annulée" };
  }
  // Recette de prod du 13/10 : une révision qui n'a produit AUCUN cycle se
  // lisait « Premier passage automatique, non revu », comme si un passage
  // existait. Rien à revoir : elle a échoué, ou elle tourne encore. (Une
  // mission en échec qui a terminé des cycles reste revisable, plus bas.)
  if (item.completedCycleCount === 0) {
    if (item.status === "failed") {
      return { color: "warning", label: "Révision en échec" };
    }
    if (item.status === "pending" || item.status === "running") {
      return { color: "primary", label: "Révision en cours" };
    }
  }

  // COUNTED by the server, not derived here (review of the review): `open` and
  // `carriedForward` are no longer disjoint — a point reopened on the previous
  // pass comes back open AND inherited — so subtracting both counted it twice and
  // could show « non revu » on a dossier someone had just worked on. This
  // letterhead is the first thing a contrôle qualité reads; it may not be wrong.
  const touched = item.humanVerdictCount > 0 || item.concludedCycleCount > 0;
  if (!touched) {
    return {
      color: "warning",
      label: "Premier passage automatique, non revu",
    };
  }

  const everythingTreated =
    item.openFindingCount === 0 &&
    item.concludedCycleCount === item.completedCycleCount;
  if (everythingTreated && item.status === "completed") {
    if (item.validatedFindingCount > 0) {
      return {
        color: "warning",
        label: `Revu : ${item.validatedFindingCount} point${
          item.validatedFindingCount > 1 ? "s" : ""
        } retenu${item.validatedFindingCount > 1 ? "s" : ""}`,
      };
    }
    return { color: "success", label: "Revu" };
  }
  if (everythingTreated) {
    // Reviewed as far as the machine got: honest about both halves.
    return {
      color: "highlight",
      label: "Cycles terminés revus, mission en échec",
    };
  }

  const parts: string[] = [];
  if (item.openFindingCount > 0) {
    parts.push(
      `${item.openFindingCount} constat${item.openFindingCount > 1 ? "s" : ""} à traiter`
    );
  }
  const cyclesToConclude = item.completedCycleCount - item.concludedCycleCount;
  if (cyclesToConclude > 0) {
    parts.push(
      `${cyclesToConclude} cycle${cyclesToConclude > 1 ? "s" : ""} à conclure`
    );
  }
  if (item.status !== "completed") {
    parts.push("mission incomplète");
  }
  return {
    color: "highlight",
    label: `Revue en cours${parts.length > 0 ? ` : ${parts.join(" · ")}` : ""}`,
  };
}
